import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import './Ceremonies.css';

interface Participant {
    name: string;
    mobile: string;
    details?: string;
    mediaUrl?: string;
}

interface Ceremony {
    _id: string;
    name: string;
    date: string;
    time: string;
    venue: string;
    dressCode?: string;
    description?: string;
    participants?: Participant[];
}

const Ceremonies: React.FC = () => {
    const { accessToken, logout, loading: authLoading } = useAuth();
    const history = useHistory();
    const [ceremonies, setCeremonies] = useState<Ceremony[]>([]);
    const [familySide, setFamilySide] = useState<'groom' | 'bride' | ''>('');
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [formData, setFormData] = useState<{
        name: string;
        date: string;
        time: string;
        venue: string;
        dressCode: string;
        description: string;
        participants: Participant[];
    }>({
        name: '',
        date: '',
        time: '',
        venue: '',
        dressCode: '',
        description: '',
        participants: [],
    });

    const fetchData = async () => {
        try {
            // Fetch Profile
            const profileRes = await fetch(`${API_BASE_URL}/api/admin/profile`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const profileData = await profileRes.json();
            if (profileRes.ok) {
                setFamilySide(profileData.admin.familySide);
            }

            // Fetch Ceremonies
            const ceremoniesRes = await fetch(`${API_BASE_URL}/api/ceremonies`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const ceremoniesData = await ceremoniesRes.json();
            if (ceremoniesRes.ok) {
                setCeremonies(ceremoniesData.ceremonies);
            } else if (ceremoniesRes.status === 401) {
                logout();
                history.push('/login');
            }
        } catch (err) {
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading) {
            if (accessToken) {
                fetchData();
            } else {
                history.push('/login');
            }
        }
    }, [accessToken, authLoading, history]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAddParticipant = () => {
        setFormData({
            ...formData,
            participants: [...formData.participants, { name: '', mobile: '', details: '', mediaUrl: '' }]
        });
    };

    const handleParticipantChange = (index: number, field: keyof Participant, value: string) => {
        const updatedParticipants = [...formData.participants];
        updatedParticipants[index] = { ...updatedParticipants[index], [field]: value };
        setFormData({ ...formData, participants: updatedParticipants });
    };

    const handleRemoveParticipant = (index: number) => {
        const updatedParticipants = formData.participants.filter((_, i) => i !== index);
        setFormData({ ...formData, participants: updatedParticipants });
    };

    const handleMediaUpload = async (index: number, file: File) => {
        const uploadData = new FormData();
        uploadData.append('media', file);

        try {
            const response = await fetch(`${API_BASE_URL}/api/ceremonies/upload-media`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}` },
                body: uploadData,
            });
            const data = await response.json();
            if (response.ok) {
                handleParticipantChange(index, 'mediaUrl', data.url);
            } else {
                alert('Upload failed');
            }
        } catch (err) {
            alert('Upload error');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const url = editingId
                ? `${API_BASE_URL}/api/ceremonies/${editingId}`
                : `${API_BASE_URL}/api/ceremonies`;

            const method = editingId ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setIsModalOpen(false);
                setEditingId(null);
                setFormData({
                    name: '',
                    date: '',
                    time: '',
                    venue: '',
                    dressCode: '',
                    description: '',
                    participants: [],
                });
                fetchData();
            } else {
                const data = await response.json();
                alert(data.message || 'Error occurred');
            }
        } catch (err) {
            alert('Server error');
        }
    };

    const handleEdit = (c: Ceremony) => {
        setEditingId(c._id);
        setFormData({
            name: c.name,
            date: c.date.split('T')[0],
            time: c.time,
            venue: c.venue,
            dressCode: c.dressCode || '',
            description: c.description || '',
            participants: c.participants || [],
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this ceremony?')) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/ceremonies/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (response.ok) {
                fetchData();
            }
        } catch (err) {
            alert('Delete failed');
        }
    };

    if (authLoading || loading) return <div className="ceremonies-container">Loading...</div>;

    return (
        <div className="admin-dashboard ceremonies-container">

            <div className="ceremonies-header">
                <div className="ceremonies-title">
                    <h1>Ceremony Schedule</h1>
                    <p>Plan and manage every beautiful moment</p>
                </div>
                <button className="add-ceremony-btn" onClick={() => setIsModalOpen(true)}>
                    <span>➕</span> Add Ceremony
                </button>
            </div>

            <div className="ceremonies-grid">
                {ceremonies.map((c) => (
                    <div key={c._id} className="ceremony-card">
                        <div className="ceremony-card-header">
                            <h2 className="ceremony-name">{c.name}</h2>
                            <div className="ceremony-actions">
                                <button className="ceremony-btn edit-btn" onClick={() => handleEdit(c)}>✏️</button>
                                <button className="ceremony-btn delete-btn" onClick={() => handleDelete(c._id)}>🗑️</button>
                            </div>
                        </div>
                        <div className="ceremony-info">
                            <div className="info-item">
                                <span className="info-icon">📅</span>
                                <div className="info-content">
                                    <span className="info-label">Date</span>
                                    <span className="info-text">{new Date(c.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                                </div>
                            </div>
                            <div className="info-item">
                                <span className="info-icon">⏰</span>
                                <div className="info-content">
                                    <span className="info-label">Time</span>
                                    <span className="info-text">{c.time}</span>
                                </div>
                            </div>
                            <div className="info-item">
                                <span className="info-icon">📍</span>
                                <div className="info-content">
                                    <span className="info-label">Venue</span>
                                    <span className="info-text">{c.venue}</span>
                                </div>
                            </div>
                            {c.dressCode && (
                                <div className="info-item">
                                    <span className="info-icon">👔</span>
                                    <div className="info-content">
                                        <span className="info-label">Dress Code</span>
                                        <span className="info-text">{c.dressCode}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {c.participants && c.participants.length > 0 && (
                            <div className="participants-section" style={{ marginTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '1.5rem' }}>
                                <h4 style={{ fontSize: '0.9rem', color: 'var(--primary-accent)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>👥</span> Participants ({c.participants.length})
                                </h4>
                                <div style={{ display: 'grid', gap: '10px' }}>
                                    {c.participants.map((p, i) => (
                                        <div key={i} style={{ background: 'rgba(255,255,255,0.03)', padding: '10px', borderRadius: '12px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ fontWeight: '600' }}>{p.name}</span>
                                                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{p.mobile}</span>
                                            </div>
                                            {p.details && <p style={{ fontSize: '0.8rem', margin: '5px 0 0', color: 'var(--text-muted)' }}>{p.details}</p>}
                                            {p.mediaUrl && (
                                                <div style={{ marginTop: '10px' }}>
                                                    <audio src={p.mediaUrl} controls style={{ width: '100%', height: '30px' }} />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>{editingId ? 'Edit Ceremony' : 'Add New Ceremony'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Name of Ceremony</label>
                                <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g. Sangeet / Mehendi" required />
                            </div>
                            <div className="form-group">
                                <label>Date</label>
                                <input type="date" name="date" value={formData.date} onChange={handleInputChange} min={new Date().toISOString().split('T')[0]} required />
                            </div>
                            <div className="form-group">
                                <label>Time</label>
                                <input type="text" name="time" value={formData.time} onChange={handleInputChange} placeholder="e.g. 7:00 PM onwards" required />
                            </div>
                            <div className="form-group">
                                <label>Venue</label>
                                <input type="text" name="venue" value={formData.venue} onChange={handleInputChange} placeholder="Full venue address" required />
                            </div>
                            <div className="form-group">
                                <label>Dress Code (Optional)</label>
                                <input type="text" name="dressCode" value={formData.dressCode} onChange={handleInputChange} placeholder="e.g. Traditional Yellow" />
                            </div>
                            <div className="form-group">
                                <label>Description (Optional)</label>
                                <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Extra details..."></textarea>
                            </div>

                            <div className="participants-form-section">
                                <div className="participants-header">
                                    <h3>Participants</h3>
                                    <button type="button" className="add-ceremony-btn" onClick={handleAddParticipant}>
                                        Manage Lineup
                                    </button>
                                </div>

                                {formData.participants.map((p, i) => (
                                    <div key={i} className="participant-form-row">
                                        <div className="participant-row-header">
                                            <span className="participant-number">Participant #{i + 1}</span>
                                            <button type="button" className="remove-participant-btn" onClick={() => handleRemoveParticipant(i)}>Remove</button>
                                        </div>
                                        <div className="form-row-grid">
                                            <div className="form-group">
                                                <label>Name</label>
                                                <input type="text" value={p.name} onChange={(e) => handleParticipantChange(i, 'name', e.target.value)} required />
                                            </div>
                                            <div className="form-group">
                                                <label>Mobile Number</label>
                                                <input type="text" value={p.mobile} onChange={(e) => handleParticipantChange(i, 'mobile', e.target.value)} required />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>Performance Details / Role (Optional)</label>
                                            <input type="text" value={p.details} onChange={(e) => handleParticipantChange(i, 'details', e.target.value)} placeholder="e.g. Solo Dance / MC" />
                                        </div>
                                        <div className="form-group">
                                            <label>Media (Song / Audio) (Optional)</label>
                                            <input type="file" accept="audio/*,video/*" onChange={(e) => e.target.files && handleMediaUpload(i, e.target.files[0])} />
                                            {p.mediaUrl && <p className="upload-status-success"><span>✅</span> Media Uploaded</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="form-actions">
                                <button type="button" className="cancel-btn" onClick={() => {
                                    setIsModalOpen(false);
                                    setEditingId(null);
                                    setFormData({ name: '', date: '', time: '', venue: '', dressCode: '', description: '', participants: [] });
                                }}>Cancel</button>
                                <button type="submit" className="submit-btn" disabled={!formData.name || !formData.date}>Save Ceremony</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Ceremonies;

